/**
 * WhatsApp Inbox API — company-side conversation viewer + reply.
 *
 * Talks to the Web-Interviewer backend (routes/whatsapp_inbox_routes.py).
 * Mirrors companyCreditsApi.js: withCredentials + signed employer/admin tokens,
 * so it authorizes cross-domain where the session cookie is dropped.
 */
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

const inboxApi = axios.create({
  baseURL: `${API_BASE_URL}/api/whatsapp`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

inboxApi.interceptors.request.use((config) => {
  try {
    const et =
      sessionStorage.getItem('employer_token') ||
      localStorage.getItem('employer_token');
    const at =
      sessionStorage.getItem('admin_token') ||
      localStorage.getItem('admin_token');
    config.headers = config.headers || {};
    if (et && !config.headers['X-Employer-Token']) config.headers['X-Employer-Token'] = et;
    if (at && !config.headers['X-Admin-Token']) config.headers['X-Admin-Token'] = at;
  } catch (e) {
    /* never break the request */
  }
  return config;
});

/**
 * List conversations (the lobby).
 * @param {'company'|'global'} scope  'global' is super-admin only.
 * @param {string} search             filter on phone / candidate email.
 * Resolves to { conversations, scope, is_super_admin }.
 */
export function listConversations({ scope = 'company', search = '' } = {}) {
  return inboxApi
    .get('/conversations', { params: { scope, search } })
    .then((r) => r.data);
}

/**
 * Full message thread for one phone.
 * Resolves to { phone, name, messages, can_free_reply, window_expires_at }.
 */
export function getThread(phone) {
  return inboxApi
    .get(`/conversations/${encodeURIComponent(phone)}`)
    .then((r) => r.data);
}

/**
 * Send a free-form reply. Only valid inside the 24h window — a 409 with
 * { error: 'outside_window', message } comes back otherwise.
 */
export function sendReply(phone, body) {
  return inboxApi
    .post(`/conversations/${encodeURIComponent(phone)}/reply`, { body })
    .then((r) => r.data);
}
